<template>
  <div class="p-4">
    <!-- Кнопки и чекбокс удалённых -->
    <div class="flex justify-between items-center mb-4">
      <div class="flex items-center gap-4">
        <div class="flex space-x-2">
        <button
          v-can-write="'products'"
          @click="openAddProductModal"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Добавить продукцию
        </button>
        <button
          v-can-write="'products'"
          @click="fetchProductsFromOneC"
          class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Запросить из 1С
        </button>
        <button
          v-if="isServiceAdmin"
          @click="isImportModalOpen = true"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Импорт из Excel
        </button>
        </div>
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="showTrashed" type="checkbox" class="rounded border-gray-300" @change="onShowTrashedChange" />
          <span class="text-sm text-gray-700">Показать удалённые</span>
        </label>
      </div>
      <router-link
        :to="{ name: 'ProductUnits' }"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Единицы измерения
      </router-link>
    </div>

    <!-- Табы по группам -->
    <div class="border-b border-gray-200 mb-4">
      <nav class="-mb-px flex flex-wrap gap-x-4 gap-y-1">
        <button
          @click="selectGroup(null)"
          :class="[
            selectedGroupId === null
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'py-2 px-1 border-b-2 font-medium text-sm'
          ]"
        >
          Все
          <span class="ml-1 text-xs text-gray-400">({{ productCounts.total }})</span>
        </button>
        <button
          v-for="group in productGroups"
          :key="group.id"
          @click="selectGroup(group.id)"
          :class="[
            selectedGroupId === group.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'py-2 px-1 border-b-2 font-medium text-sm'
          ]"
        >
          {{ group.name }}
          <span class="ml-1 text-xs text-gray-400">({{ getProductCountByGroup(group.id) }})</span>
        </button>
        <button
          @click="selectGroup('no_group')"
          :class="[
            selectedGroupId === 'no_group'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'py-2 px-1 border-b-2 font-medium text-sm'
          ]"
        >
          Без группы
          <span class="ml-1 text-xs text-gray-400">({{ productCounts.no_group }})</span>
        </button>
      </nav>
    </div>

    <!-- Поиск -->
    <div class="mb-6">
      <div class="relative max-w-md">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Поиск по названию или артикулу..."
          class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200 text-sm"
        />
        <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
          <button
            @click="clearSearch"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица продукции -->
    <table v-else class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ID</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название продукции</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Артикул</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="product in products" :key="product.id"
            :class="['border-b', showTrashed ? 'opacity-75 bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50']">
          <td class="px-4 py-2 text-sm text-gray-700">{{ product.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ product.name }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ product.sku || '-' }}</td>
          <td class="px-4 py-2 flex gap-2">
            <template v-if="showTrashed">
              <button
                v-can-write="'products'"
                @click="confirmRestore(product.id)"
                class="px-2 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Восстановить
              </button>
            </template>
            <template v-else>
              <router-link
                v-can-write="'products'"
                :to="{ name: 'ProductEdit', params: { id: product.id }, query: $route.query }"
                class="px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >Редактировать</router-link>
              <button
                v-can-write="'products'"
                @click="confirmDelete(product.id)"
                class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Удалить
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-if="pagination.total > 0"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />

    <!-- Модальное окно для добавления продукции -->
    <AddProductModal ref="addProductModal" @product-added="getProducts"></AddProductModal>

    <!-- Импорт номенклатуры из Excel — только служебный администратор -->
    <ImportProductsModal
      v-if="isImportModalOpen"
      @close="isImportModalOpen = false"
      @imported="onProductsImported"
    />

    <ConfirmModal
      v-if="showRestoreModal"
      :show="showRestoreModal"
      title="Восстановление продукции"
      message="Вы уверены, что хотите восстановить эту продукцию?"
      confirm-text="Восстановить"
      confirm-variant="success"
      @confirm="proceedRestore"
      @cancel="cancelRestore"
    />

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить эту продукцию?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddProductModal from '@/components/AddProductModal.vue';
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import Pagination from '@/components/Pagination.vue';
import ImportProductsModal from '@/components/products/ImportProductsModal.vue';
import { useAuthStore } from '@/stores/auth.js';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { AddProductModal, ConfirmModal, Pagination, ImportProductsModal },
  computed: {
    // Импорт номенклатуры из Excel — служебная функция, только для admin@admin.ru
    isServiceAdmin() {
      return useAuthStore().currentUser?.email === 'admin@admin.ru';
    },
  },
  data() {
    return {
      isImportModalOpen: false,
      products: [],
      productGroups: [],
      productCounts: { total: 0, no_group: 0, groups: [] },
      selectedGroupId: null,
      loading: true,
      isConfirmModalVisible: false,
      productIdToDelete: null,
      showRestoreModal: false,
      productIdToRestore: null,
      searchQuery: '',
      showTrashed: false,
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  created() {
    createUrlStateManager(this, {
      searchQuery: { debounce: 300, urlKey: 'search', default: '' },
      page: { type: 'number', default: 1, urlKey: 'page' },
      selectedGroupId: { urlKey: 'group', default: null },
      showTrashed: { type: 'boolean', default: false, urlKey: 'trashed' },
    }, {
      routeName: 'Products',
      onUpdate: (key) => {
        if (key === 'searchQuery') {
          this.page = 1;
          this.getProducts();
        }
      },
    });

    if (this.selectedGroupId !== null && this.selectedGroupId !== 'no_group') {
      this.selectedGroupId = parseInt(this.selectedGroupId) || null;
    }

    this.getProductGroups();
    this.getProductCounts();
    this.getProducts();
  },
  methods: {
    selectGroup(groupId) {
      this.selectedGroupId = groupId;
      this.page = 1;
      this.getProducts();
      this.getProductCounts();
    },

    onShowTrashedChange() {
      this.products = [];
      this.page = 1;
      const query = { ...this.$route.query };
      delete query.page;
      query.trashed = this.showTrashed;
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => {});
      this.getProducts();
      this.getProductCounts();
    },

    getProducts() {
      this.loading = true;
      axios
        .post('/api/product/getProducts', {
          page: this.page,
          per_page: this.perPage,
          search: this.searchQuery.trim() || undefined,
          group_id: this.selectedGroupId === 'no_group' ? 'no_group' : this.selectedGroupId,
          only_trashed: this.showTrashed,
        })
        .then((response) => {
          const d = response.data.data;
          this.products = d.data || [];
          this.pagination = {
            currentPage: d.current_page,
            lastPage: d.last_page,
            total: d.total,
            perPage: d.per_page,
          };
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },

    getProductGroups() {
      axios
        .post('/api/productGroup/getProductGroups')
        .then((response) => {
          this.productGroups = response.data.data;
        })
        .catch((error) => {
          console.error('Ошибка загрузки групп:', error);
        });
    },

    getProductCounts() {
      axios
        .post('/api/product/getProductCounts', { only_trashed: this.showTrashed })
        .then((response) => {
          this.productCounts = response.data.data;
        })
        .catch((error) => {
          console.error('Ошибка загрузки счётчиков:', error);
        });
    },

    getProductCountByGroup(groupId) {
      const g = this.productCounts.groups.find((x) => x.id === groupId);
      return g ? g.count : 0;
    },

    changePage(page) {
      this.page = page;
      this.getProducts();
    },

    confirmDelete(id) {
      this.productIdToDelete = id;
      this.isConfirmModalVisible = true;
    },

    cancelDelete() {
      this.isConfirmModalVisible = false;
      this.productIdToDelete = null;
    },

    confirmRestore(id) {
      this.productIdToRestore = id;
      this.showRestoreModal = true;
    },
    cancelRestore() {
      this.showRestoreModal = false;
      this.productIdToRestore = null;
    },
    async proceedRestore() {
      if (!this.productIdToRestore) return;
      try {
        await axios.post('/api/product/restoreProduct', { id: this.productIdToRestore });
        await this.getProducts();
        await this.getProductCounts();
      } catch (error) {
        console.error('Ошибка при восстановлении продукции:', error);
      } finally {
        this.cancelRestore();
      }
    },

    proceedDelete() {
      if (!this.productIdToDelete) {
        console.error('ID продукции не указан');
        return;
      }

      axios
        .post('/api/product/deleteProduct', { id: this.productIdToDelete })
        .then(() => {
          this.getProducts();
          this.getProductCounts();
          this.cancelDelete();
        })
        .catch((error) => {
          console.error(error);
        });
    },

    openAddProductModal() {
      if (!this.$refs.addProductModal) {
        return;
      }
      this.$refs.addProductModal.show();
    },

    fetchProductsFromOneC() {
      this.loading = true;

      axios
        .post('/api/oneC/getProducts')
        .then((response) => {
          console.log('Продукция из 1С:', response.data.data);
        })
        .catch((error) => {
          console.error('Ошибка при запросе продукции из 1С:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    clearSearch() {
      this.searchQuery = '';
    },

    onProductsImported() {
      this.getProductGroups();
      this.getProductCounts();
      this.getProducts();
    },
  },
};
</script>
